import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import NextLink from 'next/link';

import { cn } from './lib/utils';

// ---------------------------------------------------------------------------
// href routing — ported from @akademi/site-builder-runtime's `PortalLink` so
// `Button`'s `href` prop routes the same way (internal → next/link so a
// host `basePath` gets applied; external/anchor-only → plain `<a>`) without
// this package taking a dependency on site-builder-runtime itself. `next` is
// a peerDependency here, same as it already is for site-builder-blocks.
// ---------------------------------------------------------------------------

const EXTERNAL_HREF_RE = /^(https?:|mailto:|tel:|sms:)/i;

function isExternalHref(href: string): boolean {
  return EXTERNAL_HREF_RE.test(href);
}

function isAnchorOnlyHref(href: string): boolean {
  return href === '' || href === '#' || href.startsWith('#');
}

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-[var(--radius-button,var(--radius-md,6px))] border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline:
          'border-border bg-background shadow-[var(--shadow-xs)] hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        success:
          'bg-success/10! text-success! hover:bg-success/20! focus-visible:border-success/40! focus-visible:ring-success/20! dark:bg-success/20! dark:hover:bg-success/30! dark:focus-visible:ring-success/40!',
        link: 'text-primary underline-offset-4 hover:underline',
        // Tenant-brand-tokened variants, ported from the site-builder blocks
        // package's SbButton (`_shared/SbButton.tsx`) so both share the same
        // `--color-brand-*` / `--color-info-*` theme tokens.
        brand:
          'bg-[var(--color-brand-500)]! text-[var(--color-text-on-brand)]! hover:bg-[var(--color-brand-600)]',
        'brand-outlined':
          'bg-transparent border-[var(--color-brand-500)]! text-[var(--color-brand-500)]! hover:bg-[color-mix(in_srgb,var(--color-brand-500)_10%,transparent)]!',
        info: 'bg-[var(--color-accent-100)]! text-[var(--color-text-on-accent,#fff)] hover:bg-[var(--color-info-500)]/80',
      },
      size: {
        default:
          'h-9 gap-1.5 px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-button,var(--radius-md)),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1 rounded-[min(var(--radius-button,var(--radius-md)),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
        lg: 'h-10 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        icon: 'size-9',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-button,var(--radius-md)),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-8 rounded-[min(var(--radius-button,var(--radius-md)),10px)] in-data-[slot=button-group]:rounded-md',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

/** Corner-radius preset, ported from the site-builder blocks package's
 *  SbButton (`resolveSbButtonRadius`). `'default'`/undefined inherits the
 *  `--radius-button` design token baked into {@link buttonVariants}; the
 *  rest override it per-instance via inline style. */
export type ButtonRadius = 'default' | 'none' | 'sm' | 'md' | 'lg' | 'pill';

export function resolveButtonRadius(r?: ButtonRadius): string | number | undefined {
  switch (r) {
    case 'none':
      return 0;
    case 'sm':
      return 'var(--radius-sm, 4px)';
    case 'md':
      return 'var(--radius-md, 6px)';
    case 'lg':
      return 'var(--radius-lg, 10px)';
    case 'pill':
      return 999;
    default:
      return undefined;
  }
}

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  radius?: ButtonRadius;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

type ButtonAnchorProps = ButtonBaseProps &
  Omit<React.ComponentPropsWithRef<'a'>, keyof ButtonBaseProps> & {
    /** Renders as a link (internal hrefs route through `next/link` so a
     *  host `basePath` still applies; external/anchor-only hrefs render a
     *  plain `<a>`), styled identically to the button variants above. */
    href: string;
  };

export type ButtonButtonProps = ButtonBaseProps &
  Omit<React.ComponentPropsWithRef<'button'>, keyof ButtonBaseProps> & {
    href?: undefined;
  };

export type ButtonProps = ButtonAnchorProps | ButtonButtonProps;

function Button(props: ButtonProps) {
  const {
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    radius,
    style,
    ...rest
  } = props;
  const borderRadius = resolveButtonRadius(radius);
  const computedClassName = cn(buttonVariants({ variant, size, className }));
  const computedStyle = borderRadius !== undefined ? { borderRadius, ...style } : style;

  if ('href' in props && props.href !== undefined) {
    const { href, ...anchorRest } = rest as Omit<ButtonAnchorProps, keyof ButtonBaseProps>;
    const target = href;

    if (isAnchorOnlyHref(target) || isExternalHref(target)) {
      const isHttp = /^https?:/i.test(target);
      const extraAttrs =
        isExternalHref(target) && isHttp && anchorRest.target === undefined
          ? { target: '_blank', rel: anchorRest.rel ?? 'noopener noreferrer' }
          : {};
      return (
        <a
          data-slot="button"
          data-variant={variant}
          data-size={size}
          href={target}
          className={computedClassName}
          style={computedStyle}
          {...extraAttrs}
          {...anchorRest}
        />
      );
    }

    return (
      <NextLink
        data-slot="button"
        data-variant={variant}
        data-size={size}
        href={target}
        className={computedClassName}
        style={computedStyle}
        {...anchorRest}
      />
    );
  }

  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={computedClassName}
      style={computedStyle}
      {...(rest as Omit<ButtonButtonProps, keyof ButtonBaseProps>)}
    />
  );
}

export { Button, buttonVariants };
